import React, {useState, useEffect} from 'react';
import {useParams, useNavigate, Link as RouterLink} from 'react-router-dom';
import {
    Box, Typography, Paper, CircularProgress, Grid, Button, Alert,
    TextField, FormControlLabel, Switch, Snackbar, Stack,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiService from '../../services/apiService';
import ImageUploader from '../../components/ImageUploader';
import ChallengeCriteriaEditor from '../../components/ChallengeCriteriaEditor';
import EntityCriteriaEditor from '../../components/EntityCriteriaEditor';
import {ProtectedFieldSet} from '../../context/AuthDisabledContext';
import Protected from '../../components/Protected';
import {ROLES} from '../../config/roles';

const CHALLENGE_TYPES = ['daily', 'weekly', 'milestone', 'custom'];

const ChallengeEditPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const requiredAction = isNew ? 'create' : 'update';

    const [type, setType] = useState('custom');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [priority, setPriority] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [criteria, setCriteria] = useState([]);
    const [award, setAward] = useState([]);

    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState(null);
    const [showErrors, setShowErrors] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: ''});

    useEffect(() => {
        if (!isNew) {
            apiService.get(`/api/challenges/${id}`).then(data => {
                setType(data.type || 'custom');
                setName(data.name || '');
                setDescription(data.description || '');
                setImageUrl(data.imageUrl || '');
                setPriority(data.priority ?? '');
                setSortOrder(data.sortOrder ?? '');
                setIsActive(data.isActive ?? true);
                setCriteria(data.criteria || []);
                setAward(data.award || []);
            })
                .catch(err => setError(err.message || 'Failed to load data'))
                .finally(() => setLoading(false));
        }
    }, [id, isNew]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);

        const criteriaError = ChallengeCriteriaEditor.validate(criteria);
        if (criteriaError) {
            setError(`Criteria: ${criteriaError}`);
            setShowErrors(true);
            return;
        }
        // EntityCriteriaEditor.validate() deliberately accepts an empty list: the component is
        // shared with minigame entryCost, where "no cost" is legal. A challenge with no award is
        // NOT legal (ChallengeAdminService.validateAward rejects it), so without this check an
        // empty award sails past client validation and comes back as a raw 400 JSON envelope in
        // the Alert. Catch it here so it reads like the empty-Criteria message right above.
        if (!award || award.length === 0) {
            setError('Award: At least one award entry is required');
            setShowErrors(true);
            return;
        }
        const awardError = EntityCriteriaEditor.validate(award);
        if (awardError) {
            setError(`Award: ${awardError}`);
            setShowErrors(true);
            return;
        }
        setShowErrors(false);

        setIsSaving(true);
        const data = {
            id: isNew ? null : id,
            type, name, description, imageUrl,
            priority: priority === '' ? null : parseFloat(priority),
            sortOrder: sortOrder === '' ? null : parseInt(sortOrder),
            isActive,
            criteria, award,
        };

        try {
            const apiCall = isNew
                ? apiService.post('/api/challenges', data)
                : apiService.put(`/api/challenges/${id}`, data);
            await apiCall;
            setSnackbar({open: true, message: 'Challenge saved successfully!'});
            setTimeout(() => navigate('/challenges'), 800);
        } catch (err) {
            setError(err.message || 'Failed to save challenge');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;

    return (
        <Box sx={{p: 2}} component="form" onSubmit={handleSubmit}>
            <Typography variant="h4" gutterBottom>
                {isNew ? 'Create Challenge' : `Edit Challenge: ${name}`}
            </Typography>

            <Protected component={ChallengeEditPage} action={requiredAction} mode="disable">
                <ProtectedFieldSet>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 6}}>
                            <Stack spacing={3}>
                                <Paper sx={{p: 2}}>
                                    <Typography variant="h6" gutterBottom>Configuration</Typography>
                                    <Stack spacing={2}>
                                        <TextField required fullWidth label="Name" value={name}
                                                   onChange={e => setName(e.target.value)}/>
                                        <TextField fullWidth multiline rows={2} label="Description" value={description}
                                                   onChange={e => setDescription(e.target.value)}/>
                                        <FormControl fullWidth>
                                            <InputLabel>Type</InputLabel>
                                            <Select value={type} label="Type" onChange={e => setType(e.target.value)}>
                                                {CHALLENGE_TYPES.map(t => (
                                                    <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField fullWidth type="number" label="Sort Order" value={sortOrder}
                                                   onChange={e => setSortOrder(e.target.value)}
                                                   helperText="Order the mobile client lists challenges in. Leave blank for unspecified."/>
                                        <TextField fullWidth type="number" label="Priority" value={priority}
                                                   onChange={e => setPriority(e.target.value)}
                                                   slotProps={{htmlInput: {min: 0, max: 1, step: 0.1}}}
                                                   helperText="0–1. Reserved for future weighted selection — not currently used by any runtime logic."/>
                                        <FormControlLabel
                                            control={<Switch checked={isActive} onChange={e => setIsActive(e.target.checked)}/>}
                                            label="Active (assignable to players)"/>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>

                        <Grid size={{xs: 12, md: 6}}>
                            <ImageUploader
                                imageUrl={imageUrl}
                                onImageUrlChange={setImageUrl}
                                uploadPath="Challenge"
                                baseFileName={name}
                                isUploadEnabled={!!name}
                            />
                        </Grid>

                        <Grid size={12}>
                            <Paper sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Criteria</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                    What a player must do to complete this challenge. Multiple entries all need to be
                                    satisfied (AND).
                                </Typography>
                                <ChallengeCriteriaEditor
                                    value={criteria}
                                    onChange={setCriteria}
                                    excludeChallengeId={isNew ? null : id}
                                    showErrors={showErrors}
                                />
                            </Paper>
                        </Grid>

                        <Grid size={12}>
                            <Paper sx={{p: 2}}>
                                <Typography variant="h6" gutterBottom>Award</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                    What the player receives when claiming a completed challenge. Add multiple
                                    entries to award several items at once (e.g. coins + gelt).
                                </Typography>
                                <EntityCriteriaEditor value={award} onChange={setAward} preset="reward" showErrors={showErrors}/>
                            </Paper>
                        </Grid>
                    </Grid>

                    {error && <Alert severity="error" sx={{mt: 3}}>{error}</Alert>}

                    <Stack direction="row" spacing={2} sx={{mt: 3}}>
                        <Protected component={ChallengeEditPage} action={requiredAction}>
                            <Button type="submit" variant="contained"
                                    startIcon={isSaving ? <CircularProgress size={20}/> : <SaveIcon/>}
                                    disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Challenge'}
                            </Button>
                        </Protected>
                        <Button component={RouterLink} to="/challenges" variant="outlined" startIcon={<ArrowBackIcon/>}>
                            Back to List
                        </Button>
                    </Stack>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({open: false, message: ''})}
                        message={snackbar.message}
                    />
                </ProtectedFieldSet>
            </Protected>
        </Box>
    );
};

ChallengeEditPage.abilities = {
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
};

export default ChallengeEditPage;
